import { useEffect, useMemo, useState, useCallback } from "react";
import { Search, X, Loader2, ShieldCheck, Sparkles, Copy, Mail, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const PAGE_SIZE = 20;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersOffset, setUsersOffset] = useState(0);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersOffset, setOrdersOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [winback, setWinback] = useState(null);

  const loadUsers = useCallback(async (offset, q) => {
    const params = { limit: PAGE_SIZE, offset };
    if (q) params.search = q;
    const r = await api.get("/admin/users", { params });
    setUsers(r.data.users || []);
    setUsersTotal(r.data.total || 0);
    setUsersOffset(offset);
  }, []);

  const loadOrders = useCallback(async (offset) => {
    const r = await api.get("/admin/orders", { params: { limit: PAGE_SIZE, offset } });
    setOrders(r.data.orders || []);
    setOrdersTotal(r.data.total || 0);
    setOrdersOffset(offset);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadUsers(0, ""), loadOrders(0)]);
      setLoading(false);
    })();
  }, [loadUsers, loadOrders]);

  // Re-query users when search changes
  useEffect(() => {
    const t = setTimeout(() => {
      loadUsers(0, search).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [search, loadUsers]);

  const load = async () => {
    setLoading(true);
    const [u, o] = await Promise.all([
      api.get("/admin/users", { params: search ? { search } : {} }),
      api.get("/admin/orders"),
    ]);
    setUsers(u.data.users || []);
    setOrders(o.data.orders || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmtDate = (s) => {
    if (!s) return "—";
    try {
      return new Date(s).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return s;
    }
  };

  const filteredUsers = users; // server-side filtered already

  return (
    <div data-testid="admin-users">
      <span className="eyebrow">Users & orders</span>
      <h1 className="font-display text-3xl md:text-4xl text-[#1A1F3D] mt-2">Members</h1>

      <div className="mt-6 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
        <input
          className="bsp-input pl-9"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="admin-user-search"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-[#D4A853] animate-spin" /></div>
      ) : (
        <>
          <div className="mt-6 bsp-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAF8] text-[#52525b] text-left text-xs uppercase tracking-wider">
                  <tr>
                    <th className="p-3 font-semibold">Name</th>
                    <th className="p-3 font-semibold">Email</th>
                    <th className="p-3 font-semibold">Signed up</th>
                    <th className="p-3 font-semibold">Payment</th>
                    <th className="p-3 font-semibold">Last login</th>
                    <th className="p-3 font-semibold text-right">Refs</th>
                    <th className="p-3 font-semibold text-right">Images</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr
                      key={u.id}
                      onClick={() => setActive(u)}
                      className="border-t border-[#e5e7eb] hover:bg-[#FAFAF8] cursor-pointer transition-colors"
                      data-testid={`admin-user-row-${u.id}`}
                    >
                      <td className="p-3 font-medium text-[#1A1F3D]">
                        <div className="flex items-center gap-2">
                          {u.full_name}
                          {u.is_admin && <span className="text-[10px] uppercase tracking-wider font-bold text-[#D4A853] inline-flex items-center gap-0.5"><ShieldCheck className="w-3 h-3" /> Admin</span>}
                        </div>
                      </td>
                      <td className="p-3 text-[#52525b]">{u.email}</td>
                      <td className="p-3 text-[#52525b]">{fmtDate(u.created_at)}</td>
                      <td className="p-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${u.payment_status === "paid" ? "bg-[#D4A853]/20 text-[#1A1F3D]" : "bg-red-100 text-red-700"}`}>
                          {u.payment_status}
                        </span>
                      </td>
                      <td className="p-3 text-[#52525b]">{fmtDate(u.last_login)}</td>
                      <td className="p-3 text-right">{u.reference_photos_count}</td>
                      <td className="p-3 text-right">{u.generated_images_count}</td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr><td colSpan={7} className="p-6 text-center text-[#52525b]">No users found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination
            offset={usersOffset}
            total={usersTotal}
            onChange={(o) => loadUsers(o, search)}
            label="users"
            testid="users-pagination"
          />
        </>
      )}

      <h2 className="font-display text-2xl text-[#1A1F3D] mt-12">Orders</h2>
      <div className="mt-4 bsp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAF8] text-[#52525b] text-left text-xs uppercase tracking-wider">
              <tr>
                <th className="p-3 font-semibold">User</th>
                <th className="p-3 font-semibold">Email</th>
                <th className="p-3 font-semibold">Session ID</th>
                <th className="p-3 font-semibold text-right">Amount</th>
                <th className="p-3 font-semibold">Date</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 font-semibold">Win-back</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id || o.session_id} className="border-t border-[#e5e7eb]" data-testid={`order-${o.session_id}`}>
                  <td className="p-3 text-[#1A1F3D]">
                    {o.user_name}
                    {o.is_winback && <span className="ml-2 text-[10px] uppercase tracking-wider font-bold bg-[#F5E6E0] text-[#1A1F3D] px-1.5 py-0.5 rounded">Win-back</span>}
                  </td>
                  <td className="p-3 text-[#52525b]">{o.user_email}</td>
                  <td className="p-3 text-[#52525b] font-mono text-xs truncate max-w-[180px]">{o.session_id}</td>
                  <td className="p-3 text-right">£{(o.amount || 0).toFixed(2)}</td>
                  <td className="p-3 text-[#52525b]">{fmtDate(o.created_at)}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${o.payment_status === "paid" ? "bg-[#D4A853]/20 text-[#1A1F3D]" : "bg-[#F5E6E0] text-[#52525b]"}`}>
                      {o.payment_status}
                    </span>
                  </td>
                  <td className="p-3">
                    {o.payment_status === "paid" || o.is_winback ? (
                      <span className="text-xs text-[#52525b]">—</span>
                    ) : o.winback_url ? (
                      <button
                        onClick={() => setWinback(o)}
                        className="text-xs font-semibold text-[#1A1F3D] inline-flex items-center gap-1 bg-[#F5E6E0] px-2 py-1 rounded-full hover:bg-[#ecd6cd] transition-colors"
                        data-testid={`winback-view-${o.session_id}`}
                      >
                        <Check className="w-3 h-3 text-[#D4A853]" /> Sent · view link
                      </button>
                    ) : (
                      <button
                        onClick={() => setWinback(o)}
                        className="text-xs font-semibold text-[#1A1F3D] inline-flex items-center gap-1.5 bg-[#D4A853] px-3 py-1.5 rounded-full hover:bg-[#C29543] transition-colors"
                        data-testid={`winback-btn-${o.session_id}`}
                      >
                        <Sparkles className="w-3.5 h-3.5" /> Send win-back
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-[#52525b]">No orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination
        offset={ordersOffset}
        total={ordersTotal}
        onChange={loadOrders}
        label="orders"
        testid="orders-pagination"
      />

      {winback && (
        <WinbackModal
          order={winback}
          onClose={() => setWinback(null)}
          onSent={(updated) => {
            setOrders((prev) => prev.map((o) => (o.session_id === updated.session_id ? { ...o, ...updated } : o)));
          }}
        />
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-white rounded-2xl p-7 w-full max-w-lg relative" onClick={(e) => e.stopPropagation()} data-testid="user-detail-modal">
            <button onClick={() => setActive(null)} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#FAFAF8] text-[#1A1F3D] flex items-center justify-center">
              <X className="w-5 h-5" />
            </button>
            <span className="eyebrow">Member detail</span>
            <h2 className="font-display text-2xl text-[#1A1F3D] mt-1">{active.full_name}</h2>
            <div className="text-[#52525b] text-sm">{active.email}</div>
            <dl className="mt-5 grid grid-cols-2 gap-4 text-sm">
              <Detail label="User ID" value={active.id} mono />
              <Detail label="Payment status" value={active.payment_status} />
              <Detail label="Admin" value={active.is_admin ? "Yes" : "No"} />
              <Detail label="Onboarding" value={active.onboarding_complete ? "Complete" : "In progress"} />
              <Detail label="Stripe customer" value={active.stripe_customer_id || "—"} mono />
              <Detail label="Stripe payment" value={active.stripe_payment_id || "—"} mono />
              <Detail label="Reference photos" value={active.reference_photos_count} />
              <Detail label="Gallery images" value={active.generated_images_count} />
              <Detail label="Created" value={fmtDate(active.created_at)} />
              <Detail label="Last login" value={fmtDate(active.last_login)} />
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}

const Detail = ({ label, value, mono }) => (
  <div>
    <dt className="text-xs uppercase tracking-wider font-semibold text-[#52525b]">{label}</dt>
    <dd className={`mt-0.5 text-[#1A1F3D] truncate ${mono ? "font-mono text-xs" : ""}`}>{String(value)}</dd>
  </div>
);

const Pagination = ({ offset, total, onChange, label, testid }) => {
  const limit = PAGE_SIZE;
  if (total <= limit) return null;
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.ceil(total / limit);
  const prev = () => onChange(Math.max(0, offset - limit));
  const next = () => onChange(Math.min((pages - 1) * limit, offset + limit));
  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + limit, total);
  return (
    <div className="mt-4 flex items-center justify-between flex-wrap gap-3" data-testid={testid}>
      <div className="text-sm text-[#52525b]">
        Showing <span className="text-[#1A1F3D] font-medium">{start}–{end}</span> of {total} {label}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={prev}
          disabled={offset === 0}
          className="px-3 py-2 rounded-lg border border-[#e5e7eb] text-sm inline-flex items-center gap-1.5 text-[#1A1F3D] hover:bg-[#FAFAF8] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testid}-prev`}
        >
          <ChevronLeft className="w-4 h-4" /> Prev
        </button>
        <span className="text-sm text-[#52525b] px-2">Page {page} of {pages}</span>
        <button
          onClick={next}
          disabled={offset + limit >= total}
          className="px-3 py-2 rounded-lg border border-[#e5e7eb] text-sm inline-flex items-center gap-1.5 text-[#1A1F3D] hover:bg-[#FAFAF8] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testid}-next`}
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const PRESETS = [49, 59, 69];

const WinbackModal = ({ order, onClose, onSent }) => {
  const alreadySent = !!order.winback_url;
  const [amount, setAmount] = useState(alreadySent ? order.winback_amount_gbp || 49 : 49);
  const [submitting, setSubmitting] = useState(false);
  const [link, setLink] = useState(alreadySent ? order.winback_url : "");
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (alreadySent) return;
    if (!(amount >= 1 && amount <= 79)) {
      toast.error("Amount must be between £1 and £79");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/admin/orders/${order.session_id}/winback`, { amount_gbp: Number(amount) });
      setLink(r.data.url);
      onSent({
        session_id: order.session_id,
        winback_url: r.data.url,
        winback_amount_gbp: r.data.amount_gbp,
      });
      toast.success(`Win-back link generated for £${r.data.amount_gbp}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create win-back link");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy");
    }
  };

  const hasEmail = order.user_email && order.user_email !== "—";
  const mailto = () => {
    if (!hasEmail) {
      toast.error("No email on file for this order — copy the link instead.");
      return;
    }
    const subject = encodeURIComponent("A small thank-you from BrandSnapPro™");
    const body = encodeURIComponent(
      `Hi,\n\nWe noticed you started signing up for BrandSnapPro™ but didn't quite get to finish.\n\nHere's a one-time discounted link for you — £${amount} instead of £79:\n\n${link}\n\nIt expires soon, so jump in when you're ready.\n\n— The BrandSnapPro™ team`
    );
    window.location.href = `mailto:${order.user_email}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-7 w-full max-w-lg relative" onClick={(e) => e.stopPropagation()} data-testid="winback-modal">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#FAFAF8] flex items-center justify-center" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#D4A853] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#1A1F3D]" />
          </div>
          <div>
            <span className="eyebrow">Win-back coupon</span>
            <h2 className="font-display text-2xl text-[#1A1F3D]">{alreadySent ? "Win-back link" : "Create a discounted link"}</h2>
          </div>
        </div>

        <div className="mt-5 text-sm text-[#52525b]">
          For abandoned session <span className="font-mono text-xs text-[#1A1F3D] break-all">{order.session_id}</span>
          {order.user_email && order.user_email !== "—" && (
            <div className="mt-1">Customer: <span className="text-[#1A1F3D] font-medium">{order.user_email}</span></div>
          )}
        </div>

        {!alreadySent && (
          <div className="mt-5">
            <label className="text-sm font-medium text-[#1A1F3D]">Discounted price (GBP)</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setAmount(p)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    Number(amount) === p
                      ? "bg-[#1A1F3D] border-[#1A1F3D] text-white"
                      : "bg-white border-[#e5e7eb] text-[#52525b] hover:border-[#D4A853]"
                  }`}
                  data-testid={`winback-preset-${p}`}
                >
                  £{p}
                </button>
              ))}
              <input
                type="number"
                min="1"
                max="79"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bsp-input max-w-[110px]"
                data-testid="winback-amount"
              />
            </div>
            <p className="text-xs text-[#52525b] mt-2">Original price £{order.amount?.toFixed(2) || "79.00"}. Customer saves £{(79 - Number(amount || 0)).toFixed(2)}.</p>
          </div>
        )}

        {!link ? (
          <button onClick={generate} disabled={submitting} className="btn-primary mt-6 w-full justify-center" data-testid="winback-generate">
            <Sparkles className="w-4 h-4" /> {submitting ? "Generating…" : `Generate win-back link for £${amount}`}
          </button>
        ) : (
          <div className="mt-6 space-y-3">
            <label className="text-sm font-medium text-[#1A1F3D]">Discounted checkout link</label>
            <div className="p-3 rounded-lg bg-[#FAFAF8] border border-[#e5e7eb] break-all font-mono text-xs text-[#1A1F3D]" data-testid="winback-link">
              {link}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={copy} className="btn-ghost justify-center" data-testid="winback-copy">
                {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy link</>}
              </button>
              <button onClick={mailto} disabled={!hasEmail} className="btn-secondary justify-center disabled:opacity-50 disabled:cursor-not-allowed" data-testid="winback-mailto" title={hasEmail ? "" : "No email on file"}>
                <Mail className="w-4 h-4" /> Email customer
              </button>
            </div>
            {alreadySent && (
              <p className="text-xs text-[#52525b]">Already sent on {order.winback_sent_at ? new Date(order.winback_sent_at).toLocaleString("en-GB") : "—"}.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
